import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CalendarDays, Search, SlidersHorizontal, FileDown } from "lucide-react";
import { contentWidth, eyebrow, sectionHeading } from "features/landing/landingStyles";

const artworkSrc = `${process.env.PUBLIC_URL || ""}/images/landing.png`;

const workflow = [
  ["Encuentra", "Localiza cada ejercicio al instante con la búsqueda y los filtros."],
  ["Arrastra", "Distribuye los ejercicios entre los días con un simple drag & drop."],
  ["Ajusta y termina", "Completa series, repeticiones y descansos sin salir del planner."],
];

const features = [
  {
    icon: CalendarDays,
    title: "Planificación visual",
    description: "Toda la semana a la vista para avanzar más rápido.",
  },
  {
    icon: Search,
    title: "Catálogo de ejercicios",
    description: "Encuentra lo que necesitas sin recorrer listas interminables.",
  },
  {
    icon: SlidersHorizontal,
    title: "Edición flexible",
    description: "Edita uno o varios ejercicios a la vez y ahorra pasos.",
  },
  {
    icon: FileDown,
    title: "Guarda y exporta",
    description: "Guarda la rutina y genera el PDF cuando esté lista.",
  },
];

export default function FeaturesSection() {
  return (
    <Box
      component="section"
      id="funcionalidades"
      aria-labelledby="features-title"
      sx={(theme) => ({
        position: "relative",
        isolation: "isolate",
        bgcolor: "background.default",
        backgroundImage: `radial-gradient(ellipse at 0% 30%, ${alpha(
          theme.palette.primary.main,
          0.09
        )}, transparent 65%)`,
        scrollMarginTop: 24,
        "&::before, &::after": {
          content: '""',
          display: { xs: "none", sm: "block" },
          position: "absolute",
          zIndex: -1,
          top: 0,
          bottom: 0,
          width: { xs: "24%", sm: "19%", lg: "18%" },
          pointerEvents: "none",
          backgroundImage: `url("${artworkSrc}")`,
          backgroundRepeat: "no-repeat",
          backgroundSize: { xs: "auto 640px", sm: "auto 900px", lg: "auto 100%" },
          mixBlendMode: "multiply",
          opacity: { xs: 0.16, sm: 0.22, lg: 0.38 },
        },
        "&::before": {
          left: 0,
          backgroundPosition: "left top",
          maskImage: "linear-gradient(to right, black 35%, transparent)",
        },
        "&::after": {
          right: 0,
          backgroundPosition: "right bottom",
          maskImage: "linear-gradient(to left, black 35%, transparent)",
        },
      })}
    >
      <Box sx={{ ...contentWidth, py: { xs: 8, md: 13 } }}>
        <Typography
          sx={{
            ...eyebrow,
            color: "primary.main",
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            "&::before": { content: '""', width: 24, height: 2, bgcolor: "accent.main" },
          }}
        >
          DE CERO A RUTINA EN MINUTOS
        </Typography>
        <Typography
          id="features-title"
          component="h2"
          sx={{ ...sectionHeading, maxWidth: 700, fontWeight: 700 }}
        >
          Menos tiempo planificando.
          <Box component="span" sx={{ display: "block", color: "primary.main" }}>
            Más tiempo entrenando.
          </Box>
        </Typography>
        <Box
          sx={(theme) => ({
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            mt: { xs: 4, md: 6 },
            p: { xs: 1, md: 2 },
            bgcolor: "dark.main",
            backgroundImage: `radial-gradient(ellipse at 100% 0%, ${alpha(
              theme.palette.primary.main,
              0.35
            )}, transparent 70%)`,
            borderRadius: 4,
            border: "1px solid",
            borderColor: "grey.700",
            boxShadow: `0 20px 48px -20px ${alpha(theme.palette.dark.main, 0.4)}`,
          })}
        >
          {workflow.map(([title, description], index) => (
            <Box
              key={title}
              sx={(theme) => ({
                p: { xs: 3, md: 4 },
                position: "relative",
                "&:not(:last-child)::after": {
                  content: '""',
                  position: "absolute",
                  bgcolor: alpha(theme.palette.white.main, 0.12),
                  bottom: { xs: 0, md: 32 },
                  right: { xs: 24, md: 0 },
                  left: { xs: 24, md: "auto" },
                  top: { xs: "auto", md: 32 },
                  height: { xs: "1px", md: "auto" },
                  width: { xs: "auto", md: "1px" },
                },
              })}
            >
              <Typography
                aria-hidden="true"
                sx={(theme) => ({
                  fontSize: "3.5rem",
                  fontWeight: 700,
                  lineHeight: 1,
                  letterSpacing: "-0.06em",
                  color: alpha(theme.palette.white.main, 0.22),
                  mb: 4,
                })}
              >
                0{index + 1}
              </Typography>
              <Box
                aria-hidden="true"
                sx={{ width: 24, height: 3, bgcolor: "accent.main", borderRadius: 1, mb: 2 }}
              />
              <Typography
                component="h3"
                sx={{ color: "white.main", fontSize: "1.25rem", fontWeight: 600, mb: 1.5 }}
              >
                {title}
              </Typography>
              <Typography sx={{ color: "grey.300", fontSize: "0.9375rem", lineHeight: 1.7 }}>
                {description}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          component="ul"
          aria-label="Funcionalidades de Gymplanner"
          sx={{
            listStyle: "none",
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
            },
            gap: 2,
            mt: 3,
            p: 0,
          }}
        >
          {features.map(({ icon: Icon, title, description }) => (
            <Box
              component="li"
              key={title}
              sx={(theme) => ({
                display: "flex",
                alignItems: "flex-start",
                gap: 2.5,
                p: { xs: 2.5, md: 3.5 },
                borderRadius: 3,
                bgcolor: alpha(theme.palette.light.main, 0.94),
                border: "1px solid",
                borderColor: alpha(theme.palette.primary.main, 0.13),
                transition:
                  "background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
                "@media (hover: hover)": {
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.065),
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    boxShadow: `0 8px 24px -12px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                },
              })}
            >
              <Box
                sx={(theme) => ({
                  color: "primary.main",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.09),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                })}
              >
                <Icon size={22} strokeWidth={1.6} aria-hidden="true" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="h3"
                  sx={{ fontSize: "0.9375rem", fontWeight: 600, mb: 0.75 }}
                >
                  {title}
                </Typography>
                <Typography sx={{ color: "text.secondary", lineHeight: 1.7 }}>
                  {description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
